import { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload as UploadIcon, Link2, ExternalLink, X, RotateCcw, Image as ImageIcon, AlertCircle } from "lucide-react";
import { TRACKS, resourceKindsFor, sessionTitle } from "@/lib/tracks";

const TOTAL_SLOTS = TRACKS.reduce((n, t) => n + Array.from({ length: t.count }).reduce((m, _, i) => m + resourceKindsFor(t.key, i).length, 0), 0);
const GRID_COLS = { 1: "sm:grid-cols-2", 4: "sm:grid-cols-2 lg:grid-cols-4", 5: "sm:grid-cols-3 lg:grid-cols-5" };

function SourceBadge({ source }) {
  if (!source) return null;
  const isOverride = source === "override";
  return (
    <span
      className="text-[0.5rem] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full shrink-0"
      style={{ background: isOverride ? "var(--terracotta)" : "var(--surface-hover)", color: isOverride ? "#FFF" : "var(--muted)" }}
    >
      {isOverride ? "Override" : "Global"}
    </span>
  );
}

function SlotCell({ label, url, source, canReset, busy, onSaveLink, onClear, testid }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(url || "");
  useEffect(() => { setVal(url || ""); }, [url, editing]);

  const commit = async () => {
    const t = val.trim();
    if (t && !/^https?:\/\//i.test(t)) { toast.error("Link must start with http:// or https://"); return; }
    await onSaveLink(t);
    setEditing(false);
  };

  return (
    <div className="rounded-md p-2 flex flex-col gap-1.5 min-w-0" style={{ border: `1px ${url ? "solid" : "dashed"} ${url ? "var(--gold)" : "var(--border-subtle)"}`, background: url ? "var(--surface-hover)" : "transparent", opacity: busy ? 0.6 : 1 }} data-testid={testid}>
      <div className="flex items-center justify-between gap-1">
        <span className="text-[0.6rem] uppercase tracking-[0.12em] font-medium truncate" style={{ color: url ? "var(--gold)" : "var(--muted)" }}>{label}</span>
        <SourceBadge source={source} />
      </div>
      {editing ? (
        <div className="flex gap-1">
          <input autoFocus type="url" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }} placeholder="https://…" className="flex-1 min-w-0 text-[0.7rem] px-1.5 py-0.5" data-testid={`${testid}-input`} />
          <button onClick={commit} className="text-[0.6rem] px-2 rounded" style={{ background: "var(--ink)", color: "var(--bg-base)" }} data-testid={`${testid}-save`}>Save</button>
        </div>
      ) : url ? (
        <a href={url} target="_blank" rel="noreferrer" className="text-[0.7rem] truncate flex items-center gap-1 hover:underline" style={{ color: "var(--ink-2)" }} title={url} data-testid={`${testid}-link`}>
          <ExternalLink size={10} className="shrink-0" /> <span className="truncate">{url.includes("/api/files/lesson/") ? "Uploaded file" : url.replace(/^https?:\/\//, "")}</span>
        </a>
      ) : (
        <span className="text-[0.7rem] italic" style={{ color: "var(--muted)" }}>Not set</span>
      )}
      <div className="flex items-center gap-1 flex-wrap">
        <button onClick={() => setEditing((v) => !v)} className="flex items-center gap-1 text-[0.6rem] px-1.5 py-0.5 rounded hover:bg-[var(--bg-base)] transition-colors" style={{ color: "var(--ink-2)", border: "1px solid var(--border-subtle)" }} data-testid={`${testid}-edit`}>
          <Link2 size={9} /> {url ? "Edit link" : "Add link"}
        </button>
        {canReset && (
          <button onClick={onClear} className="flex items-center gap-1 text-[0.6rem] px-1.5 py-0.5 rounded hover:bg-[var(--bg-base)] transition-colors" style={{ color: "var(--terracotta)" }} data-testid={`${testid}-clear`} title={source === "override" ? "Remove override — use the global link" : "Remove link"}>
            {source === "override" ? <><RotateCcw size={9} /> Use global</> : <><X size={9} /> Remove</>}
          </button>
        )}
      </div>
    </div>
  );
}

const notesKey = (trackKey, i) => `${trackKey}:${i}:${trackKey === "first_steps" ? "teacher_notes" : "notes"}`;

function NoNotesMarker({ testid }) {
  return (
    <span className="inline-flex items-center gap-1 text-[0.55rem] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full shrink-0" style={{ border: "1px dashed var(--terracotta)", color: "var(--terracotta)" }} data-testid={testid}>
      <AlertCircle size={9} /> No notes yet
    </span>
  );
}

function TrackSection({ track, scope, globalRes, overrides, busyKey, onSaveLink }) {
  const isGlobal = scope === "global";
  const hasNotes = (i) => track.key === "first_steps" || !resourceKindsFor(track.key, i).some((k) => notesKey(track.key, i).endsWith(`:${k.key}`)) || !!(isGlobal ? globalRes[notesKey(track.key, i)] : (overrides?.[notesKey(track.key, i)] || globalRes[notesKey(track.key, i)]));
  const missing = Array.from({ length: track.count }).filter((_, i) => !hasNotes(i)).length;
  return (
    <section className="mb-10" data-testid={`library-track-${track.key}`}>
      <div className="flex items-baseline gap-3 mb-3">
        <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>{track.label}</h3>
        <span className="text-xs" style={{ color: "var(--muted)" }}>{track.count} lessons</span>
        {missing > 0 && <span className="text-xs" style={{ color: "var(--terracotta)" }} data-testid={`library-missing-count-${track.key}`}>· {missing} without notes</span>}
      </div>
      <div className="space-y-2">
        {Array.from({ length: track.count }).map((_, i) => { const kinds = resourceKindsFor(track.key, i); return (
          <div key={i} className="rounded-md p-3 flex flex-col md:flex-row md:items-start gap-3" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid={`library-row-${track.key}-${i}`}>
            <div className="md:w-56 shrink-0 flex items-start gap-2">
              <span className="text-[0.6rem] uppercase tracking-wider font-medium mt-0.5" style={{ color: "var(--muted)" }}>{i + 1}</span>
              <div className="flex flex-col items-start gap-1 min-w-0">
                <span className="text-sm leading-tight" style={{ color: "var(--ink)" }}>{sessionTitle(track.key, i)}</span>
                {!hasNotes(i) && <NoNotesMarker testid={`library-no-notes-${track.key}-${i}`} />}
              </div>
            </div>
            <div className={`grid gap-2 flex-1 min-w-0 grid-cols-1 ${GRID_COLS[kinds.length] || "sm:grid-cols-2"}`}>
              {kinds.map((k) => {
                const key = `${track.key}:${i}:${k.key}`;
                const ov = overrides?.[key];
                const gl = globalRes[key];
                const url = isGlobal ? gl : (ov || gl);
                const source = isGlobal ? null : ov ? "override" : gl ? "global" : null;
                return (
                  <SlotCell
                    key={k.key}
                    label={k.label}
                    url={url || ""}
                    source={source}
                    canReset={isGlobal ? !!gl : !!ov}
                    busy={busyKey === key}
                    onSaveLink={(u) => onSaveLink(key, u)}
                    onClear={() => onSaveLink(key, "")}
                    testid={`slot-${track.key}-${i}-${k.key}`}
                  />
                );
              })}
            </div>
          </div>
        ); })}
      </div>
    </section>
  );
}

function FlyerCard({ kind, info, version, onReplace, onReset, busy }) {
  const fileRef = useRef(null);
  return (
    <div className="rounded-md p-4 flex gap-4" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid={`flyer-card-${kind}`}>
      <img src={`${API}/files/flyer/${kind}?v=${version}`} alt={info.label} className="w-28 h-36 object-cover rounded shrink-0" style={{ border: "1px solid var(--border-subtle)" }} data-testid={`flyer-preview-${kind}`} />
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="font-medium text-sm" style={{ color: "var(--ink)" }}>{info.label}</div>
        <div className="text-xs mt-1" style={{ color: "var(--muted)" }} data-testid={`flyer-status-${kind}`}>
          {info.custom ? `Custom: ${info.filename || "uploaded image"}` : "Using the default artwork"}
        </div>
        {kind === "inquiry" && (
          <p className="text-[0.65rem] mt-2" style={{ color: "var(--muted)" }}>Each church's QR code is stamped in the center white square, so keep that square in the same spot on new art.</p>
        )}
        <div className="mt-auto pt-3 flex gap-2 flex-wrap">
          <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-ghost text-xs" style={{ padding: "0.4rem 0.8rem" }} data-testid={`flyer-replace-${kind}`}>
            <UploadIcon size={12} /> {busy ? "Uploading…" : "Replace image"}
          </button>
          {info.custom && (
            <button onClick={onReset} className="btn-ghost text-xs" style={{ padding: "0.4rem 0.8rem", color: "var(--terracotta)" }} data-testid={`flyer-reset-${kind}`}>
              <RotateCcw size={12} /> Restore default
            </button>
          )}
        </div>
        <input ref={fileRef} type="file" className="hidden" accept="image/png,image/jpeg,image/webp" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onReplace(f); }} data-testid={`flyer-file-input-${kind}`} />
      </div>
    </div>
  );
}

export default function AdminResourceLibrary() {
  const [data, setData] = useState(null);
  const scope = "global";
  const [busyKey, setBusyKey] = useState(null);
  const [flyerVersion, setFlyerVersion] = useState(0);

  useEffect(() => {
    api.get("/admin/resource-library").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load the resource library."));
  }, []);

  const org = useMemo(() => data?.orgs.find((o) => o.id === scope), [data, scope]);
  const filled = useMemo(() => {
    if (!data) return 0;
    const eff = scope === "global" ? data.global : { ...data.global, ...(org?.overrides || {}) };
    return Object.values(eff).filter(Boolean).length;
  }, [data, scope, org]);

  const run = async (key, fn, okMsg) => {
    setBusyKey(key);
    try { const r = await fn(); setData(r.data); if (okMsg) toast.success(okMsg); }
    catch (err) { toast.error(err?.response?.data?.detail || "Something went wrong."); }
    finally { setBusyKey(null); }
  };

  const saveLink = (key, url) => run(key, () => api.put("/admin/resource-library/link", { scope, key, url }), url ? "Link saved." : "Link removed.");
  const replaceFlyer = async (kind, file) => {
    const fd = new FormData(); fd.append("file", file);
    await run(`flyer:${kind}`, () => api.post(`/admin/resource-library/flyers/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } }), "Flyer updated.");
    setFlyerVersion((v) => v + 1);
  };
  const resetFlyer = async (kind) => {
    await run(`flyer:${kind}`, () => api.delete(`/admin/resource-library/flyers/${kind}`), "Default flyer restored.");
    setFlyerVersion((v) => v + 1);
  };

  if (!data) return <p style={{ color: "var(--muted)" }} data-testid="library-loading">Loading resource library…</p>;

  return (
    <div data-testid="admin-resource-library">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h2 className="font-serif-display text-2xl tracking-tight" style={{ color: "var(--ink)" }}>Resource library</h2>
          <p className="mt-1 text-sm max-w-2xl" style={{ color: "var(--muted)" }}>
            The one master place for every lesson link. Every church uses these links automatically, so update a link here and it changes everywhere.
          </p>
        </div>
        <div className="text-right" data-testid="library-filled-count">
          <div className="text-3xl font-serif-display leading-none" style={{ color: "var(--ink)" }}>{filled}<span className="text-base" style={{ color: "var(--muted)" }}> / {TOTAL_SLOTS}</span></div>
          <div className="text-[0.6rem] uppercase tracking-[0.2em] mt-1" style={{ color: "var(--muted)" }}>links filled</div>
        </div>
      </div>


      {TRACKS.map((t) => (
        <TrackSection key={t.key} track={t} scope={scope} globalRes={data.global} overrides={org?.overrides} busyKey={busyKey} onSaveLink={saveLink} />
      ))}

      <section className="mt-12 pt-8" style={{ borderTop: "1px solid var(--border-subtle)" }} data-testid="library-flyers">
        <div className="flex items-center gap-2 mb-4">
          <ImageIcon size={16} style={{ color: "var(--gold)" }} />
          <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Printable flyers</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(data.flyers).map(([kind, info]) => (
            <FlyerCard key={kind} kind={kind} info={info} version={flyerVersion} busy={busyKey === `flyer:${kind}`} onReplace={(f) => replaceFlyer(kind, f)} onReset={() => resetFlyer(kind)} />
          ))}
        </div>
      </section>
    </div>
  );
}
